import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Button, IconButton, Typography, Alert } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';

const AudioRecordingModal = ({ open, handleClose, onRecordingComplete }) => {
  const [status, setStatus] = useState('idle'); // 'idle', 'recording', 'stopped'
  const [timer, setTimer] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [permissionError, setPermissionError] = useState('');
  const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [audioKey, setAudioKey] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const hardResetAudioElement = () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } catch (e) {
      // noop
    }
    try {
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    } catch (e) {
      // noop
    }
  };

  const stopAndClearStream = () => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (e) {
        // noop
      }
      streamRef.current = null;
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (status === 'recording') {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    } else if (status === 'idle') {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [status]);

  // Request microphone permission when modal opens
  useEffect(() => {
    const requestMicrophonePermission = async () => {
      if (!open) return;

      try {
        setPermissionError('');

        // Check if we can query permissions (not supported in all browsers)
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const permission = await navigator.permissions.query({ name: 'microphone' });
            setPermissionStatus(permission.state);

            permission.addEventListener('change', () => {
              setPermissionStatus(permission.state);
            });
          } catch (e) {
            console.log('Permissions API not fully supported, using fallback');
          }
        }

        // Always try to get microphone access when modal opens
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });

        setPermissionStatus('granted');
        
        // Store stream for later use
        streamRef.current = stream;

      } catch (error) {
        console.error('Microphone permission error:', error);
        setPermissionStatus('denied');

        if (error.name === 'NotAllowedError') {
          setPermissionError('Microphone access denied. Please allow microphone access and try again.');
        } else if (error.name === 'NotFoundError') {
          setPermissionError('No microphone found. Please connect a microphone and try again.');
        } else {
          setPermissionError('Unable to access microphone. Please check your microphone settings.');
        }
      }
    };

    requestMicrophonePermission();

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [open]);

  const startRecording = async () => {
    try {
      // If we already have a preview loaded, fully reset the <audio> element + URL.
      // WebKit can otherwise keep the element in an error state between recordings.
      hardResetAudioElement();
      if (mediaBlobUrl) {
        try {
          URL.revokeObjectURL(mediaBlobUrl);
        } catch (e) {
          // noop
        }
        setMediaBlobUrl(null);
        setRecordedBlob(null);
        setAudioKey((k) => k + 1);

        // After playback, iOS Safari can get stuck if we keep the same stream.
        stopAndClearStream();
      }

      // Get fresh stream if needed
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      chunksRef.current = [];

      // Create MediaRecorder WITHOUT specifying mimeType
      // Let the browser choose its native format
      const mediaRecorder = new MediaRecorder(streamRef.current);
      
      console.log('🎤 MediaRecorder created with mimeType:', mediaRecorder.mimeType);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Create blob with the ACTUAL mime type from the recorder
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        
        console.log('🎤 Recording stopped - blob.type:', blob.type);
        console.log('🎤 Recording stopped - blob.size:', blob.size);
        
        setRecordedBlob(blob);
        
        // Create URL for preview
        const url = URL.createObjectURL(blob);
        setMediaBlobUrl(url);
        setAudioKey((k) => k + 1);
        
        setStatus('stopped');
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Request data every 1000ms (1 second) - required for Safari
      setStatus('recording');

    } catch (error) {
      console.error('Error starting recording:', error);
      setPermissionError('Failed to start recording. Please try again.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleSave = () => {
    if (recordedBlob) {
      onRecordingComplete(recordedBlob);
      handleClose();
      
      // Cleanup
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
      setMediaBlobUrl(null);
      setRecordedBlob(null);
      setStatus('idle');
    }
  };

  const handleModalClose = () => {
    // Cleanup
    if (mediaBlobUrl) {
      URL.revokeObjectURL(mediaBlobUrl);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setMediaBlobUrl(null);
    setRecordedBlob(null);
    setStatus('idle');
    handleClose();
  };
  
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60).toString().padStart(2, '0');
    const seconds = (time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Record Audio
        </Typography>

        {permissionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {permissionError}
          </Alert>
        )}

        {permissionStatus === 'denied' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2, textAlign: 'center' }}>
              Microphone access is required to record audio.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Please allow microphone access in your browser settings and refresh the page.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IconButton
              onClick={status === 'recording' ? stopRecording : startRecording}
              disabled={permissionStatus === 'denied' || status === 'stopped'}
              sx={{
                backgroundColor: status === 'recording' ? '#9447AF' : '#00A7CD',
                color: 'white',
                width: 80,
                height: 80,
                '&:hover': {
                  backgroundColor: status === 'recording' ? '#7d3f94' : '#008c9e',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#a0a0a0',
                },
              }}
            >
              {status === 'recording' ? <StopIcon sx={{ fontSize: 50 }} /> : <MicIcon sx={{ fontSize: 50 }} />}
            </IconButton>
            <Typography sx={{ mt: 2 }}>{formatTime(timer)}</Typography>
            {mediaBlobUrl && (
              <audio
                key={audioKey}
                ref={audioRef}
                src={mediaBlobUrl}
                controls
                playsInline
                style={{ marginTop: "20px" }}
              />
            )}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleModalClose} sx={cancelButtonStyle}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!recordedBlob || permissionStatus === 'denied'} sx={saveButtonStyle}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const cancelButtonStyle = {
  color: "#00A7CD",
  borderColor: "#00A7CD",
  textTransform: 'none',
  marginRight: 1,
  '&:hover': {
    borderColor: '#008c9e',
    backgroundColor: 'rgba(0, 167, 205, 0.04)'
  }
};

const saveButtonStyle = {
  backgroundColor: "#00A7CD",
  color: "#fff",
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#008c9e'
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#a0a0a0'
  }
};

export default AudioRecordingModal;
